import type { PlaylistItem, PopRecord, ScreenDefaultItem } from "../api/types";

// Clock-driven slot scheduling. Playback position is a pure function of the
// (server-adjusted) wall clock, so every screen on the same cycle plays the
// same slot at the same instant, and a crash/restart resumes mid-cycle
// correctly with no drift.

export interface SlotPosition {
  slotIndex: number;
  slotStartMs: number;
  slotEndMs: number;
  cycleLengthSec: number;
}

export interface CurrentPlay {
  item: PlaylistItem | null;
  position: SlotPosition;
}

export function getSlotPosition(
  nowMs: number,
  slotsPerCycle: number,
  slotDurationSec: number,
): SlotPosition {
  const cycleLengthSec = Math.max(1, slotsPerCycle * slotDurationSec);
  const epochSec = nowMs / 1000;
  // Positive modulo, robust to clock offsets producing negatives.
  const posInCycleSec = ((epochSec % cycleLengthSec) + cycleLengthSec) % cycleLengthSec;
  const slotIndex = Math.min(slotsPerCycle - 1, Math.floor(posInCycleSec / slotDurationSec));
  const offsetIntoSlotSec = posInCycleSec - slotIndex * slotDurationSec;
  const slotStartMs = nowMs - offsetIntoSlotSec * 1000;
  const slotEndMs = slotStartMs + slotDurationSec * 1000;
  return { slotIndex, slotStartMs, slotEndMs, cycleLengthSec };
}

/**
 * Client-side validity check. The server already filters the playlist by
 * window, but the DEVICE must enforce it too: a player running from a cached
 * schedule (offline, or 304-stable) would otherwise replay an ended campaign
 * forever — the server can only take content away from a device it can reach.
 * Windowless items (legacy cache snapshots) pass — server filtering was their
 * only contract. (No playout_math.py mirror: the backend's now-playing inputs
 * come pre-filtered from the DB window query, so only the client needs this.)
 */
export function itemInWindow(p: PlaylistItem, nowMs: number): boolean {
  if (p.window_start) {
    const start = new Date(p.window_start).getTime();
    if (!Number.isNaN(start) && nowMs < start) return false;
  }
  if (p.window_end) {
    const end = new Date(p.window_end).getTime();
    if (!Number.isNaN(end) && nowMs >= end) return false;
  }
  return true;
}

export function resolveItemForSlot(
  playlist: PlaylistItem[],
  slotIndex: number,
  nowMs: number,
): PlaylistItem | null {
  return (
    playlist.find((p) => p.slot_index === slotIndex && itemInWindow(p, nowMs)) ?? null
  );
}

export function getCurrentPlay(
  nowMs: number,
  playlist: PlaylistItem[],
  slotsPerCycle: number,
  slotDurationSec: number,
): CurrentPlay {
  const position = getSlotPosition(nowMs, slotsPerCycle, slotDurationSec);
  return { item: resolveItemForSlot(playlist, position.slotIndex, nowMs), position };
}

// Absolute cycle number on the (server-adjusted) wall clock. Stateless and
// shared by every screen on the same clock — keeps the throttle deterministic
// and crash-safe (a restart resumes the same play/skip pattern).
export function cycleIndexFor(nowMs: number, cycleLengthSec: number): number {
  return Math.floor(nowMs / 1000 / Math.max(1, cycleLengthSec));
}

// Even-spaced (Bresenham) decision: about `fraction` of cycles return true, with
// no long runs. Deterministic from (cycleIndex, fraction). fraction >= 1 (or NaN)
// always plays; <= 0 never plays. e.g. 0.281 over 720 cycles → ~202 trues, spread.
export function shouldPlayThisCycle(cycleIndex: number, fraction: number): boolean {
  if (!(fraction < 1)) return true;
  if (fraction <= 0) return false;
  return Math.floor((cycleIndex + 1) * fraction) > Math.floor(cycleIndex * fraction);
}

// Bonus fill: the item to show when a slot has no contracted play this cycle
// (unsold index, or its owner throttled out). Deterministic rotation by
// (cycleIndex + slotIndex) over the WHOLE in-window playlist, so every active
// booking shares the free airtime and the rotation advances across cycles —
// no item owns the same free slot forever, and a screen with any content
// never goes dark. Keep in lockstep with playout_math.pick_bonus_index.
export function pickBonus(
  pool: PlaylistItem[],
  cycleIndex: number,
  slotIndex: number,
): PlaylistItem | null {
  if (pool.length === 0) return null;
  const n = pool.length;
  return pool[(((cycleIndex + slotIndex) % n) + n) % n];
}

// What to actually render this tick. A contracted slot item plays only on its
// allotted cycles (throttle); otherwise (no contracted item, or throttled out)
// the bonus rotation fills the gap from the whole playlist; a screen with no
// active bookings at all shows its default creative. `channel` distinguishes
// them: contracted records PoP against its own booking_slot index, bonus
// against the cycle slot it filled, the screen default never (it has no
// booking to evidence).
export type PlayChannel = "contracted" | "bonus" | "default";

export interface ResolvedPlay {
  media: PlaylistItem | ScreenDefaultItem | null;
  channel: PlayChannel;
  position: SlotPosition;
}

/**
 * Play boundary: a new slot started, or the media changed mid-slot (schedule
 * update). A media_id-change-only boundary would collapse consecutive plays of
 * the same creative (single-slot screens, duplicate media in adjacent slots, a
 * single-item playlist) into one endless "play" — no PoP, frozen slot index.
 */
export function playChanged(prev: ResolvedPlay | null, next: ResolvedPlay): boolean {
  if (!prev) return true;
  if (prev.position.slotStartMs !== next.position.slotStartMs) return true;
  return (prev.media?.media_id ?? null) !== (next.media?.media_id ?? null);
}

/**
 * Proof-of-play record for the play that just ended, or null when the ended
 * slot showed nothing. Both channels are evidenced: contracted plays bind to
 * their booking_slot index, bonus fills to the cycle slot they occupied (the
 * backend splits the two at read time by slot ownership — bonus can only ever
 * soften money through the capped make-good). Duration is capped at the slot
 * end so suspended tickers (hidden tab, sleep) don't inflate evidence for time
 * that was never rendered.
 */
export function popForEndedPlay(
  prev: ResolvedPlay | null,
  playedFromMs: number,
  nowMs: number,
): PopRecord | null {
  if (!prev?.media) return null;
  // The screen default has no booking to evidence — the backend would reject
  // its record as device-fabricated. Play it, never PoP it.
  if (prev.channel === "default") return null;
  const endedAtMs = Math.min(nowMs, prev.position.slotEndMs);
  return {
    booking_id: (prev.media as PlaylistItem).booking_id,
    media_id: prev.media.media_id,
    slot_index: prev.channel === "contracted"
      ? (prev.media as PlaylistItem).slot_index
      : prev.position.slotIndex,
    played_at: new Date(playedFromMs).toISOString(),
    actual_duration_ms: Math.max(0, endedAtMs - playedFromMs),
    status: "played",
  };
}

export function resolvePlay(
  nowMs: number,
  playlist: PlaylistItem[],
  slotsPerCycle: number,
  slotDurationSec: number,
  screenDefault: ScreenDefaultItem | null = null,
): ResolvedPlay {
  const position = getSlotPosition(nowMs, slotsPerCycle, slotDurationSec);
  const contracted = resolveItemForSlot(playlist, position.slotIndex, nowMs);
  const cycleIndex = cycleIndexFor(nowMs, position.cycleLengthSec);
  if (contracted && shouldPlayThisCycle(cycleIndex, contracted.play_rate_fraction ?? 1)) {
    return { media: contracted, channel: "contracted", position };
  }
  // Free/throttled slot: one sequence over every in-window item — contracted
  // slots always beat bonus, but no slot with available content ever goes dark.
  const pool = playlist.filter((p) => itemInWindow(p, nowMs));
  const bonus = pickBonus(pool, cycleIndex, position.slotIndex);
  if (bonus) {
    return { media: bonus, channel: "bonus", position };
  }
  return { media: screenDefault, channel: "default", position };
}
